import { Outlet, Navigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { useAuthStore } from '@/stores/authStore'
import { useCurrentUser } from '@/hooks/useAuth'
import { Spinner } from '@/components/ui/spinner'

interface LayoutProps {
  requireAuth?: boolean
  requireAdmin?: boolean
}

export function Layout({ requireAuth = true, requireAdmin = false }: LayoutProps) {
  const { isAuthenticated, user } = useAuthStore()
  const { isLoading, isError, error } = useCurrentUser()

  const isAuthFailure = isError && (error as { status?: number })?.status === 401

  // Show spinner while auth check is in progress or retrying after network error
  if (requireAuth && !isAuthenticated && (isLoading || (isError && !isAuthFailure))) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  // Redirect to login only on confirmed auth failure
  if (requireAuth && !isAuthenticated && (isAuthFailure || !isLoading)) {
    return <Navigate to="/login" replace />
  }

  // If admin is required but user is not admin, redirect to dashboard
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Header />
        <main className="min-h-0 flex-1 overflow-auto bg-muted/30 p-[14px] md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
